import i18n from 'src/i18n/i18n';

// App language codes that aren't valid BCP 47 locales.
const LOCALE_BY_LANGUAGE: Record<string, string> = {
  ae: 'ar',
  cn: 'zh'
};

const FALLBACK_LOCALE = 'en';

export const getNumberLocale = (language: string = i18n.language): string =>
  LOCALE_BY_LANGUAGE[language] ?? language ?? FALLBACK_LOCALE;

const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (
  locale: string,
  options: Intl.NumberFormatOptions
): Intl.NumberFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat(locale, options);
    } catch {
      formatter = new Intl.NumberFormat(FALLBACK_LOCALE, options);
    }
    formatterCache.set(key, formatter);
  }
  return formatter;
};

const DEFAULT_OPTIONS: Intl.NumberFormatOptions = { maximumFractionDigits: 2 };

/** `value` when it is a finite number, otherwise `fallback`. */
export const toFiniteNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/** A percentage clamped to 0-100; non-finite input reads as 0. */
export const clampPercentage = (value: unknown): number =>
  Math.min(100, Math.max(0, toFiniteNumber(value)));

/** `part` as a percentage of `whole`; 0 when `whole` is not above 0. */
export const percentOf = (part: number, whole: number): number =>
  whole > 0 ? (part / whole) * 100 : 0;

/**
 * Formats a number with the current language's decimal and thousands
 * separators (e.g. 1234567.89 -> "1,234,567.89" in en, "1.234.567,89" in de).
 * Non-numeric input renders as-is so callers can pass through placeholders
 * such as "--".
 */
export const formatNumber = (
  value: number | string | null | undefined,
  options: Intl.NumberFormatOptions = DEFAULT_OPTIONS,
  locale: string = getNumberLocale()
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) {
    return String(value);
  }
  return getFormatter(locale, options).format(n);
};

const COMPACT_UNITS = [
  { threshold: 1e12, suffix: 'T' },
  { threshold: 1e9, suffix: 'B' },
  { threshold: 1e6, suffix: 'M' },
  { threshold: 1e3, suffix: 'k' }
];

/**
 * Shortens anything at or above 1,000 to a `k` / `M` / `B` / `T` suffix with
 * up to `fractionDigits` decimals, using the current language's decimal
 * separator ("1.5k" in en, "1,5k" in de). Below 1,000 it keeps up to two
 * decimals. Rounding that crosses a unit boundary is promoted, so 999999
 * renders "1M" rather than "1000k".
 *
 * With `fixed`, every value - below 1,000 too - shows exactly
 * `fractionDigits` decimals ("1,0k", "12,0"), like numeral's "0.0a".
 *
 * Deliberately not `Intl.NumberFormat`'s `notation: 'compact'`: under de / it
 * that leaves thousands uncompacted and emits long suffixes ("1,2 Mio.").
 */
export const formatCompactNumber = (
  value: number | string | null | undefined,
  fractionDigits = 1,
  {
    fixed = false,
    locale = getNumberLocale()
  }: { fixed?: boolean; locale?: string } = {}
): string => {
  const scaledOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: fixed ? fractionDigits : 0,
    maximumFractionDigits: fractionDigits
  };
  const smallOptions = fixed ? scaledOptions : DEFAULT_OPTIONS;

  const n = Number(value);
  if (!Number.isFinite(n)) {
    return formatNumber(0, smallOptions, locale);
  }

  const rounded = Math.round(n * 100) / 100;
  const abs = Math.abs(rounded);
  const factor = 10 ** fractionDigits;
  const scaleTo = (threshold: number) =>
    Math.round((rounded / threshold) * factor) / factor;

  const index = COMPACT_UNITS.findIndex((u) => abs >= u.threshold);
  if (index === -1) {
    return formatNumber(rounded, smallOptions, locale);
  }

  let unit = COMPACT_UNITS[index];
  let scaled = scaleTo(unit.threshold);
  if (Math.abs(scaled) >= 1e3 && index > 0) {
    unit = COMPACT_UNITS[index - 1];
    scaled = scaleTo(unit.threshold);
  }

  return `${formatNumber(scaled, scaledOptions, locale)}${unit.suffix}`;
};

/** Formats bytes as B / KB / MB / GB / TB with locale-aware decimals. */
export const formatFileSize = (
  bytes: number,
  fractionDigits = 1,
  locale: string = getNumberLocale()
): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return `${formatNumber(0, DEFAULT_OPTIONS, locale)} B`;
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  return `${formatNumber(
    bytes / 1024 ** i,
    { maximumFractionDigits: i === 0 ? 0 : fractionDigits },
    locale
  )} ${units[i]}`;
};

export const getNumberSeparators = (
  locale: string = getNumberLocale()
): { group: string; decimal: string } => {
  const parts = getFormatter(locale, {}).formatToParts(1234567.8);
  return {
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.'
  };
};

const toLatinDigits = (input: string) =>
  input
    // Arabic-Indic
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    // Extended Arabic-Indic (Persian / Urdu)
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

/**
 * Parses a number typed in the current language's format, e.g. "1.234,56"
 * in de or "1,234.56" in en -> 1234.56. Thousands separators are ignored
 * wherever they appear, so partially typed values ("1.") parse too - which
 * also means "0.7" typed under de reads as 7, since "." groups there.
 * Returns NaN for anything that isn't a number.
 */
export const parseLocaleNumber = (
  input: string | number | null | undefined,
  locale: string = getNumberLocale()
): number => {
  if (typeof input === 'number') {
    return input;
  }
  if (input === null || input === undefined) {
    return NaN;
  }
  const { group, decimal } = getNumberSeparators(locale);
  let normalized = toLatinDigits(input.trim())
    .replace(/−/g, '-')
    // Arabic thousands / decimal separators, whichever digits the locale uses.
    .replace(/٬/g, group)
    .replace(/٫/g, decimal);
  // Locales that group with a (narrow / no-break) space also accept a plain one.
  normalized = /\s/.test(group)
    ? normalized.replace(/\s/g, '')
    : normalized.split(group).join('');
  normalized = normalized.split(decimal).join('.');

  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(normalized)) {
    return NaN;
  }
  return Number(normalized);
};

/**
 * Normalizes whole-number input typed with the current language's thousands
 * separators ("1.234" in de, "1,234" in en) to plain digits ("1234"), so it
 * can be stored and parsed like before. Returns null when the text isn't a
 * non-negative whole number, so the caller can reject the keystroke.
 */
export const normalizeIntegerInput = (
  input: string,
  locale: string = getNumberLocale()
): string | null => {
  if (input === '') {
    return '';
  }
  const n = parseLocaleNumber(input, locale);
  return Number.isSafeInteger(n) && n >= 0 ? String(n) : null;
};

/** Blocks typing the current language's decimal separator in a whole-number field. */
export const preventDecimalSeparatorKey = (
  event: { key: string; preventDefault: () => void },
  locale: string = getNumberLocale()
) => {
  if (event.key === getNumberSeparators(locale).decimal) {
    event.preventDefault();
  }
};

/** ag-grid `valueFormatter` for numeric columns. */
export const numberValueFormatter = ({ value }: { value?: unknown }): string =>
  formatNumber(value as number | string | null | undefined);

/** Money amount with exactly two decimals, e.g. "1.234,50" in de. */
export const formatAmount = (
  value: number | string | null | undefined
): string =>
  formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
